// Relative dynamics measure — a pure leaf (no engine state), unit-tested like
// difficulty.ts. Scores how well the player's velocity *shape* (accents / contour)
// tracks the expected dynamics, NOT the absolute level — so it is invariant to the
// keyboard's velocity curve and the player's overall loudness (calibration-proof).
// See docs/superpowers/specs/2026-06-13-expression-coach-design.md §4.3.

const EPSILON = 1e-6;

function variance(xs: readonly number[], mean: number): number {
  return xs.reduce((s, x) => s + (x - mean) ** 2, 0) / xs.length;
}

/**
 * Relative dynamics, 0..1, for an aligned span of player vs. expected onset
 * velocities. `1` = the shapes match (or there is nothing to grade); `0` = the
 * player ignored or inverted the dynamics.
 *
 * - Flat expected line (no dynamic intent) → `1` (nothing to grade).
 * - Player flat against a varying expected line → `0` (the "flat-bar" flaw).
 * - Otherwise the Pearson correlation of the two shapes, mapped from [-1,1] to [0,1].
 */
export function relativeDynamics(player: readonly number[], expected: readonly number[]): number {
  const n = player.length;
  if (n === 0 || n !== expected.length) return 1; // nothing to grade

  const meanP = player.reduce((s, x) => s + x, 0) / n;
  const meanE = expected.reduce((s, x) => s + x, 0) / n;
  const varE = variance(expected, meanE);
  if (varE < EPSILON) return 1; // expected is flat — no dynamics asked for
  const varP = variance(player, meanP);
  if (varP < EPSILON) return 0; // player is flat against a shaped line — the flaw

  let cov = 0;
  for (let i = 0; i < n; i++) {
    const p = player[i] ?? meanP;
    const e = expected[i] ?? meanE;
    cov += (p - meanP) * (e - meanE);
  }
  cov /= n;
  const r = cov / Math.sqrt(varP * varE); // Pearson correlation in [-1, 1]
  return Math.max(0, Math.min(1, (r + 1) / 2));
}
